/**
 * Sessie 171 (aanvulling JP 16-10, herstel na recensie): de knop
 * "Retourlabel" in de zaak.
 *
 * Een tik op "Retourlabel" maakt nog niets aan. Hij opent in de zaak een
 * compact blok met:
 *
 * (a) de bestelling waar het om gaat ("Bestelling 48217 van 10 okt,
 *     1 artikel"); heeft de klant meer bestellingen, dan een keuzelijst van
 *     de laatste vijf uit de bestellingen-tab van de klantkaart;
 * (b) de reden (standaard leeg, zie STANDAARD_REDEN_ZAAK) en geopend ja/nee;
 * (c) zodra die gekozen zijn: de uitkomst vooraf in één zin, met op de plek
 *     het vinkje "Toch terugnemen" als de uitsluiting opzij mag;
 * (d) één knop met de echte gevolgen in de tekst: "Label maken en mailen"
 *     bij label, "Aanmaken, gaat naar beoordeling" bij beoordeling, en geen
 *     aanmaakknop bij niet aangenomen zonder vinkje.
 *
 * Reden "voldoet niet aan verwachtingen" vraagt twee extra vragen; die staat
 * hier niet in de lijst, daarvoor is "Anders invullen" (het volle formulier).
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  adminRetourenApi,
  vertaalApiFout,
  type AdminRetourAanmaakAntwoord,
  type AdminRetourAanmaakBody,
  type AdminRetourOrderzoekAntwoord,
  type AdminRetourUitkomstVooraf,
  type RetourRedenWaarde,
} from "@/lib/api";

import { retourLink } from "../../Admin/retourLink";
import type { V6Zaak } from "../api";
import { kaart10Api, kaart10Sleutels } from "../Rechts/kaart10/api";

/**
 * De reden die het blok vooraf kiest. `null` = JP kiest elke keer zelf.
 * Kiest JP later een vaste reden (bijv. "herroepingsrecht"), dan is dat
 * deze ene regel en is het weer bijna één tik.
 */
export const STANDAARD_REDEN_ZAAK: RetourRedenWaarde | null = null;

export const ZAAK_TOELICHTING = "Retour aangemaakt vanuit de zaak.";

/** Redenen die in de zaak te kiezen zijn, met hun naam. */
export const ZAAK_REDENEN: { waarde: RetourRedenWaarde; naam: string }[] = [
  { waarde: "herroepingsrecht", naam: "Herroepingsrecht" },
  { waarde: "verkeerd_product_besteld", naam: "Verkeerd product besteld" },
  { waarde: "verkeerd_product_geleverd", naam: "Verkeerd product geleverd" },
  { waarde: "pakket_beschadigd_bij_aankomst", naam: "Pakket beschadigd bij aankomst" },
  { waarde: "verpakking_beschadigd_bij_aankomst", naam: "Verpakking gescheurd/kapot bij aankomst" },
  { waarde: "product_defect_kapot", naam: "Product defect/kapot" },
  { waarde: "product_te_laat_geleverd", naam: "Product te laat geleverd" },
  { waarde: "allergische_reactie", naam: "Allergische reactie" },
  { waarde: "andere_reden", naam: "Andere reden" },
];

/** Bij deze redenen bepaalt geopend de uitkomst (zie retour_beslisboom). */
const GEOPEND_TELT: RetourRedenWaarde[] = ["allergische_reactie", "andere_reden"];

const MAX_BESTELLINGEN = 5;

interface BestellingKeuze {
  nummer: string;
  besteld_op: string | null;
  artikelen: number;
}

type Stand =
  | { soort: "dicht" }
  | { soort: "open" }
  | { soort: "bezig" }
  | { soort: "gemaakt"; retour: AdminRetourAanmaakAntwoord }
  | { soort: "fout"; tekst: string };

interface VoorafKlaar {
  basis: AdminRetourUitkomstVooraf;
  metToch: AdminRetourUitkomstVooraf | null;
  body: AdminRetourAanmaakBody;
}

type Vooraf =
  | { soort: "leeg" }
  | { soort: "laden" }
  | ({ soort: "klaar" } & VoorafKlaar)
  | { soort: "fout"; tekst: string };

export function datumKort(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" }).replace(/\.$/, "");
}

export function bestellingZin(b: BestellingKeuze): string {
  const datum = datumKort(b.besteld_op);
  const artikelen = `${b.artikelen} ${b.artikelen === 1 ? "artikel" : "artikelen"}`;
  return datum
    ? `Bestelling ${b.nummer} van ${datum}, ${artikelen}`
    : `Bestelling ${b.nummer}, ${artikelen}`;
}

function voorafZin(vooraf: AdminRetourUitkomstVooraf): string {
  const reden = vooraf.reden ?? "geen reden doorgegeven";
  if (vooraf.uitkomst === "label") return "Valt binnen de regels: er komt meteen een retourlabel.";
  if (vooraf.uitsluiting === "geopend") return "Valt buiten de regels: geopende verpakking.";
  if (vooraf.uitsluiting === "termijn") return "Valt buiten de regels: de retourtermijn is verstreken.";
  if (vooraf.uitkomst === "beoordeling") return `Gaat naar beoordeling: ${reden}. Er komt nog geen label.`;
  return `Valt buiten de regels: ${reden}.`;
}

/** Mag JP deze uitsluiting met het vinkje opzij zetten? */
function overschrijfbaar(vooraf: AdminRetourUitkomstVooraf): boolean {
  if (vooraf.uitkomst !== "niet_aangenomen") return false;
  if (vooraf.uitsluiting === "termijn") return true;
  return vooraf.uitsluiting === "geopend" && vooraf.geopend_overschrijfbaar;
}

function uitkomstZin(retour: AdminRetourAanmaakAntwoord): string {
  const reden = retour.reden ?? "geen reden doorgegeven";
  switch (retour.uitkomst) {
    case "label_gemaakt":
      return "Label gemaakt en gemaild.";
    case "label_mislukt":
      return `Retour ${retour.retournummer} vastgelegd, label mislukt: ${retour.label_fout ?? "onbekende fout"}.`;
    case "beoordeling":
      return `Retour ${retour.retournummer} staat op beoordeling: ${reden}. Er is nog geen label.`;
    default:
      return `Niet aangenomen: ${reden}. Er is geen label gemaakt.`;
  }
}

function maakBody(
  order: AdminRetourOrderzoekAntwoord,
  reden: RetourRedenWaarde,
  geopend: boolean,
): AdminRetourAanmaakBody {
  return {
    order_id: order.order_id,
    artikelen: order.artikelen.map((artikel, index) => ({
      artikel_index: index,
      aantal: Math.max(1, artikel.aantal),
      reden,
      verpakking_geopend: geopend,
      toelichting: ZAAK_TOELICHTING,
      toelichting_keuze: null,
      omschrijving_duidelijk: null,
    })),
    oplossing: "terugbetaling",
    termijn_overschrijven: false,
    geopend_overschrijven: false,
    klant_niet_mailen: false,
    ook_geen_statusmails: false,
  };
}

function metVinkje(body: AdminRetourAanmaakBody, basis: AdminRetourUitkomstVooraf): AdminRetourAanmaakBody {
  return {
    ...body,
    termijn_overschrijven: basis.termijn_verstreken,
    geopend_overschrijven: basis.geopend_overschrijfbaar,
  };
}

export function RetourlabelZaak({ zaak }: { zaak: V6Zaak }) {
  const [stand, setStand] = useState<Stand>({ soort: "dicht" });
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [reden, setReden] = useState<RetourRedenWaarde | "">(STANDAARD_REDEN_ZAAK ?? "");
  const [geopend, setGeopend] = useState<boolean | null>(null);
  const [toch, setToch] = useState(false);

  const laatste = zaak.klantkaart?.laatste_bestelling ?? null;
  const meer = (zaak.klantkaart?.aantal_bestellingen ?? 0) > 1 && !!zaak.klant_id;
  const open = stand.soort !== "dicht";
  const mailadres =
    zaak.via?.aan ?? zaak.kanalen?.find((k) => k.kanaal === "mail")?.adres ?? null;

  const bestellingenQuery = useQuery({
    queryKey: kaart10Sleutels.bestellingen(zaak.klant_id ?? ""),
    queryFn: () => kaart10Api.bestellingen(zaak.klant_id as string),
    enabled: open && meer,
    staleTime: 60_000,
  });

  const keuzes: BestellingKeuze[] = useMemo(() => {
    const lijst: BestellingKeuze[] = [];
    if (laatste) {
      lijst.push({
        nummer: laatste.nummer,
        besteld_op: laatste.besteld_op,
        artikelen: laatste.regels.reduce((n, r) => n + Math.max(1, r.aantal), 0),
      });
    }
    for (const o of bestellingenQuery.data?.orders ?? []) {
      if (o.geannuleerd || lijst.some((b) => b.nummer === o.ordernummer)) continue;
      lijst.push({ nummer: o.ordernummer, besteld_op: o.besteld_op, artikelen: o.producten.length });
    }
    return lijst.slice(0, MAX_BESTELLINGEN);
  }, [laatste, bestellingenQuery.data]);

  const ordernummer = gekozen ?? laatste?.nummer ?? null;
  const huidige = keuzes.find((b) => b.nummer === ordernummer) ?? null;
  const geopendNodig = reden !== "" && GEOPEND_TELT.includes(reden);
  const compleet = reden !== "" && (!geopendNodig || geopend !== null);

  // De uitkomst vooraf: een nieuwe ronde zodra bestelling, reden, geopend of het vinkje wijzigt.
  const voorafAan = open && stand.soort !== "gemaakt" && !!ordernummer && !!mailadres && compleet;
  const voorafQuery = useQuery({
    queryKey: ["v6", "zaak-retourlabel", ordernummer, mailadres, reden, geopendNodig ? geopend : null, toch],
    enabled: voorafAan,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<VoorafKlaar> => {
      const order = await adminRetourenApi.orderzoek(ordernummer as string, mailadres as string);
      if (order.artikelen.length === 0) {
        throw new Error("Deze bestelling heeft geen artikelregels om terug te nemen.");
      }
      const body = maakBody(order, reden as RetourRedenWaarde, geopendNodig ? Boolean(geopend) : false);
      const basis = await adminRetourenApi.uitkomstVooraf(body);
      const metToch =
        toch && overschrijfbaar(basis) ? await adminRetourenApi.uitkomstVooraf(metVinkje(body, basis)) : null;
      return { basis, metToch, body };
    },
  });
  const vooraf: Vooraf = !voorafAan
    ? { soort: "leeg" }
    : voorafQuery.isError
      ? {
          soort: "fout",
          tekst:
            voorafQuery.error instanceof Error && !("isAxiosError" in voorafQuery.error)
              ? voorafQuery.error.message
              : vertaalApiFout(voorafQuery.error, "De uitkomst is niet op te halen."),
        }
      : voorafQuery.data
        ? { soort: "klaar", ...voorafQuery.data }
        : { soort: "laden" };

  if (!laatste || !ordernummer) return null;
  const andersLink = retourLink({ ordernummer, mailadres });

  async function aanmaken(body: AdminRetourAanmaakBody) {
    setStand({ soort: "bezig" });
    try {
      const retour = await adminRetourenApi.aanmaken(body);
      setStand({ soort: "gemaakt", retour });
    } catch (e) {
      setStand({ soort: "fout", tekst: vertaalApiFout(e, "Aanmaken is niet gelukt.") });
    }
  }

  // Welke uitkomst geldt nu, en welke knop hoort daarbij?
  let geldend: AdminRetourUitkomstVooraf | null = null;
  let aanmaakBody: AdminRetourAanmaakBody | null = null;
  if (vooraf.soort === "klaar") {
    if (vooraf.metToch && toch) {
      geldend = vooraf.metToch;
      aanmaakBody = metVinkje(vooraf.body, vooraf.basis);
    } else {
      geldend = vooraf.basis;
      aanmaakBody = vooraf.body;
    }
  }
  const knopTekst =
    geldend?.uitkomst === "label"
      ? "Label maken en mailen"
      : geldend?.uitkomst === "beoordeling"
        ? "Aanmaken, gaat naar beoordeling"
        : null;

  return (
    <div className="v6-retourlabel" data-testid="zaak-retourlabel">
      <div className="v6-retourlabel-rij">
        <button
          type="button"
          className="v6-knop"
          data-testid="zaak-retourlabel-knop"
          disabled={!mailadres}
          aria-expanded={open}
          title={mailadres ? "Retour voor deze klant voorbereiden" : "Geen e-mailadres van de klant bekend"}
          onClick={() => {
            setStand(open ? { soort: "dicht" } : { soort: "open" });
            setToch(false);
          }}
        >
          Retourlabel
        </button>
        {!mailadres ? (
          <Link to={andersLink} className="stil" data-testid="zaak-retourlabel-anders">
            Anders invullen
          </Link>
        ) : null}
      </div>

      {open && stand.soort !== "gemaakt" ? (
        <div className="v6-retourlabel-uitkomst" data-testid="zaak-retourlabel-blok">
          {keuzes.length > 1 ? (
            <label className="v6-retourlabel-veld">
              <span className="stil">Bestelling</span>
              <select
                value={ordernummer}
                data-testid="zaak-retourlabel-bestelling"
                onChange={(e) => {
                  setGekozen(e.target.value);
                  setToch(false);
                }}
              >
                {keuzes.map((b) => (
                  <option key={b.nummer} value={b.nummer}>
                    {bestellingZin(b)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p data-testid="zaak-retourlabel-bestelzin">
              {huidige ? bestellingZin(huidige) : `Bestelling ${ordernummer}`}
              {meer ? (
                <>
                  {" "}
                  <Link to={andersLink} className="stil" data-testid="zaak-retourlabel-andere-bestelling">
                    Andere bestelling
                  </Link>
                </>
              ) : null}
            </p>
          )}

          <div className="v6-retourlabel-rij">
            <label className="v6-retourlabel-veld">
              <span className="stil">Reden</span>
              <select
                value={reden}
                data-testid="zaak-retourlabel-reden"
                onChange={(e) => {
                  setReden(e.target.value as RetourRedenWaarde | "");
                  setToch(false);
                }}
              >
                <option value="">Kies een reden</option>
                {ZAAK_REDENEN.map((r) => (
                  <option key={r.waarde} value={r.waarde}>
                    {r.naam}
                  </option>
                ))}
              </select>
            </label>
            {geopendNodig ? (
              <span className="v6-retourlabel-rij" role="radiogroup" aria-label="Verpakking geopend">
                <span className="stil">Geopend</span>
                <label>
                  <input
                    type="radio"
                    name={`geopend-${zaak.zaak_id}`}
                    checked={geopend === true}
                    data-testid="zaak-retourlabel-geopend-ja"
                    onChange={() => {
                      setGeopend(true);
                      setToch(false);
                    }}
                  />{" "}
                  ja
                </label>
                <label>
                  <input
                    type="radio"
                    name={`geopend-${zaak.zaak_id}`}
                    checked={geopend === false}
                    data-testid="zaak-retourlabel-geopend-nee"
                    onChange={() => {
                      setGeopend(false);
                      setToch(false);
                    }}
                  />{" "}
                  nee
                </label>
              </span>
            ) : null}
          </div>

          {vooraf.soort === "laden" ? <p className="stil">Uitkomst ophalen…</p> : null}
          {vooraf.soort === "fout" ? (
            <p role="alert" data-testid="zaak-retourlabel-fout">
              {vooraf.tekst}
            </p>
          ) : null}

          {vooraf.soort === "klaar" && geldend ? (
            <div data-testid="zaak-retourlabel-keuze" data-uitkomst={geldend.uitkomst}>
              <p data-testid="zaak-retourlabel-zin">{voorafZin(vooraf.basis)}</p>
              {overschrijfbaar(vooraf.basis) ? (
                <label className="v6-retourlabel-vinkje">
                  <input
                    type="checkbox"
                    checked={toch}
                    data-testid="zaak-retourlabel-toch"
                    onChange={(e) => setToch(e.target.checked)}
                  />
                  Toch terugnemen (ik beslis zelf)
                </label>
              ) : null}
              {toch && vooraf.metToch ? (
                <p className="stil" data-testid="zaak-retourlabel-zin-toch">
                  Met het vinkje: {voorafZin(vooraf.metToch)}
                </p>
              ) : null}
              {geldend.uitkomst === "beoordeling" && knopTekst ? (
                <p className="stil">De klant krijgt een aanmeldbevestiging.</p>
              ) : null}
            </div>
          ) : null}

          <div className="v6-retourlabel-rij">
            {knopTekst && aanmaakBody ? (
              <button
                type="button"
                className="v6-knop doe"
                data-testid="zaak-retourlabel-aanmaken"
                disabled={stand.soort === "bezig"}
                onClick={() => void aanmaken(aanmaakBody)}
              >
                {stand.soort === "bezig" ? "Bezig…" : knopTekst}
              </button>
            ) : null}
            <Link to={andersLink} data-testid="zaak-retourlabel-anders">
              Anders invullen
            </Link>
          </div>
          {stand.soort === "fout" ? (
            <p role="alert" data-testid="zaak-retourlabel-fout">
              {stand.tekst}
            </p>
          ) : null}
        </div>
      ) : null}

      {stand.soort === "gemaakt" ? (
        <div
          className="v6-retourlabel-uitkomst"
          role="status"
          data-testid="zaak-retourlabel-uitkomst"
          data-uitkomst={stand.retour.uitkomst}
        >
          <span>{uitkomstZin(stand.retour)}</span>{" "}
          <Link
            to={`/retouren?retour=${encodeURIComponent(stand.retour.id)}`}
            data-testid="zaak-retourlabel-dossier"
          >
            Naar het dossier
          </Link>
        </div>
      ) : null}
    </div>
  );
}
